import {
  useCallback,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { InputSpec, Item } from '@guess/engine';
import { cx } from '../ui/cx';
import type { AnswerProps } from './AnswerRenderer';
import s from './input.module.scss';

type OrderSpec = Extract<InputSpec, { kind: 'order' }>;

/**
 * La remise en ordre.
 *
 * Ecrit a la main sur les Pointer Events, sans librairie. Un seul jeu
 * d'evenements couvre la souris, le doigt et le stylet, et `setPointerCapture`
 * garantit qu'un geste rapide qui sort de la liste continue d'etre suivi au
 * lieu de rester coince a mi-chemin.
 *
 * Il y a AUSSI un deplacement au clavier, et ce n'est pas une politesse : ce
 * mini-jeu accorde du credit partiel, donc quelques paires bien placees
 * rapportent. Quelqu'un qui ne peut pas glisser doit pouvoir marquer.
 *
 * Le composant ne juge rien. Il renvoie un ordre d'identifiants ; c'est le
 * juge serveur qui compte les paires (Kendall) et decide de la precision.
 */
export function OrderAnswer({ spec, disabled, onSubmit }: AnswerProps<OrderSpec>) {
  const [order, setOrder] = useState<Item[]>(spec.items);
  const [dragId, setDragId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const rowsRef = useRef(new Map<string, HTMLLIElement>());
  // Position du pointeur au dernier point de reference. Remise a zero a chaque
  // permutation, sinon l'element continuerait de s'eloigner de son rang.
  const anchorRef = useRef(0);

  const move = useCallback((from: number, to: number) => {
    setOrder((current) => {
      if (to < 0 || to >= current.length || from === to) return current;
      const next = [...current];
      const [item] = next.splice(from, 1);
      if (!item) return current;
      next.splice(to, 0, item);
      return next;
    });
  }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLLIElement>, id: string) => {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    anchorRef.current = event.clientY;
    setDragId(id);
    setOffset(0);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLLIElement>) => {
    if (dragId === null) return;
    const delta = event.clientY - anchorRef.current;
    setOffset(delta);

    const index = order.findIndex((item) => item.id === dragId);
    if (index < 0) return;

    // On compare le pointeur au MILIEU de la rangee voisine : permuter des
    // qu'on l'effleure ferait vibrer la liste, attendre de la depasser
    // entierement donnerait une impression de retard.
    const neighbourId = order[delta < 0 ? index - 1 : index + 1]?.id;
    if (neighbourId === undefined) return;
    const neighbour = rowsRef.current.get(neighbourId)?.getBoundingClientRect();
    if (!neighbour) return;

    const crossed =
      delta < 0 ? event.clientY < neighbour.top + neighbour.height / 2
                : event.clientY > neighbour.top + neighbour.height / 2;

    if (crossed) {
      move(index, delta < 0 ? index - 1 : index + 1);
      anchorRef.current = event.clientY;
      setOffset(0);
    }
  };

  const endDrag = () => {
    setDragId(null);
    setOffset(0);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLLIElement>, index: number) => {
    if (disabled) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const to = event.key === 'ArrowUp' ? index - 1 : index + 1;
    move(index, to);
    // Le focus suit l'element, pas le rang : sinon une pression maintenue
    // ferait descendre des elements differents a chaque repetition.
    const id = order[index]?.id;
    if (id !== undefined) {
      window.requestAnimationFrame(() => rowsRef.current.get(id)?.focus());
    }
  };

  return (
    <div className={s.orderStage}>
      <ol className={s.orderList}>
        {order.map((item, index) => (
          <li
            key={item.id}
            ref={(node) => {
              if (node) rowsRef.current.set(item.id, node);
              else rowsRef.current.delete(item.id);
            }}
            className={cx(s.orderRow, dragId === item.id && s.orderRowDragged)}
            style={dragId === item.id ? { transform: `translate3d(0, ${offset}px, 0)` } : undefined}
            onPointerDown={(event) => onPointerDown(event, item.id)}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={(event) => onKeyDown(event, index)}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-label={`${item.label}, rang ${index + 1} sur ${order.length}. Fleches haut et bas pour deplacer.`}
          >
            <span className={s.orderRank} aria-hidden="true">
              {index + 1}
            </span>
            {item.imageUrl ? <img className={s.orderThumb} src={item.imageUrl} alt="" /> : null}
            <span className={s.orderLabel}>{item.label}</span>
            <span className={s.orderGrip} aria-hidden="true">
              ::
            </span>
          </li>
        ))}
      </ol>

      <button
        type="button"
        className={s.submit}
        disabled={disabled}
        onClick={() => onSubmit({ kind: 'order', order: order.map((item) => item.id) })}
      >
        Valider cet ordre
      </button>
    </div>
  );
}
